import cn from "classnames";
import cloneDeep from "lodash/cloneDeep";
import { useMemo } from "react";
import BackgroundVideo from "@/components/common/BackgroundVideo";
import HeaderVariant1 from "@/components/site-builder/BaseModule/HeaderVariant1";
import HorizontalHeader from "@/components/site-builder/BaseModule/HorizontalHeader";
import BackgroundImage from "@/components/site-builder/BaseModule/ModuleUI/BackgroundImage";
import BackgroundOverlay from "@/components/site-builder/BaseModule/ModuleUI/BackgroundOverlay";
import Wrapper from "@/components/site-builder/BaseModule/Wrapper";
import {
  SiteBuilderModuleHeaderAlignment,
  SiteBuilderModuleLayoutSize,
} from "@/constants/site-builder";
import { useGetHEXColorByColorVariant } from "@/hooks/useGetHEXColorByColorVariant";
import { getSizeByPredefinedSize } from "@/utils/style/getSizeByPredefinedSize";
import type { HeroModuleType } from "../types";
import s2 from "../HeroVariant2/HeroVariant2.module.scss";
import s from "./HeroVariant3.module.scss";

export interface HeroVariant3Props {
  module: HeroModuleType;
}

export default function HeroVariant2(props: HeroVariant3Props) {
  const { module } = props;
  const {
    style: {
      display: { layoutSize, headerAlignment },
      background: {
        color: { colorVariant },
        image: imageSettings,
        video: videoSettings,
        overlay: overlaySettings,
      },
    },
    settings: {
      container: { minHeight },
    },
    body: { image },
  } = module;

  // Disable base module background settings
  const moduleClone = useMemo(() => {
    const clone = cloneDeep(module);
    clone.style.background.video.enabled = false;
    clone.style.background.overlay.enabled = false;

    return clone;
  }, [module]);

  const tileColor = useGetHEXColorByColorVariant(colorVariant);
  const tileStyle = {
    minHeight: getSizeByPredefinedSize(minHeight),
    backgroundColor: tileColor || undefined,
  };
  const contentClassName = cn(s.content, {
    "flex-center": headerAlignment === SiteBuilderModuleHeaderAlignment.Middle,
  });

  return (
    <Wrapper module={moduleClone} className={s2.wrapper}>
      <HorizontalHeader
        module={module}
        headerContent={
          <div className={s2.tile} style={tileStyle}>
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <HeaderVariant1 module={module} />
            </div>
          </div>
        }
        fluid={layoutSize === SiteBuilderModuleLayoutSize.FullScreen}
        stretchColumns
        collapseTabletMobileGap
      >
        <div className={contentClassName}>
          <div
            className={s.mobileImage}
            style={{ aspectRatio: image.aspectRatio }}
          />
          {image.source && (
            <BackgroundImage
              settings={{ ...imageSettings, enabled: true, image }}
            />
          )}
          {videoSettings.enabled && videoSettings.videoId && (
            <BackgroundVideo
              source={videoSettings.source}
              videoId={videoSettings.videoId}
            />
          )}
          {overlaySettings.enabled && (
            <BackgroundOverlay settings={overlaySettings} />
          )}
        </div>
      </HorizontalHeader>
    </Wrapper>
  );
}
